/**
 * `useRoomController(factory)` — the generic React adapter for a
 * {@link RemoteController}, shared by the per-domain `useRemoteRoster` / `useEncounter`
 * hooks.
 *
 * It owns the whole controller↔React bridge, once:
 *   - open the connection via the injected `factory` in an effect (and close it on
 *     unmount / dependency change);
 *   - bridge the socket lifecycle into a `connStatus` and flip to `"connected"`
 *     when the controller's welcome handshake lands;
 *   - keep the latest controller in a ref so the action callbacks stay stable
 *     across the connecting→connected swap without going stale;
 *   - adapt the controller's subscribe/snapshot into React via
 *     `useSyncExternalStore`;
 *   - **optionally retain the last WELCOMED state across a controller swap** — a
 *     reconnect builds a *fresh*, unwelcomed controller (see the effect below). Its
 *     snapshot is real but meaningless (every `RemoteController` subclass seeds a
 *     non-null placeholder history before its first welcome), so retention is gated on
 *     `controller.connected`, not on `snapshot.state` being non-null. Without retention,
 *     `state` flashes back to that placeholder on every drop, forcing the caller's
 *     surface to unmount rather than show a reconnecting board. Opt-in
 *     (`options.retainStateAcrossReconnect`); both shipped rooms take it. See
 *     `retainedStateRef` below for the deps-vs-reconnect scoping that keeps a retaining room
 *     from leaking one identity's board under another's id;
 *   - **refuse writes while not connected** (`dispatch`/`undo`/`redo`, see `dispatch`'s doc)
 *     and **report the link to the app-wide pill** (`useLinkReport`, `lib/linkHealth.tsx`) —
 *     the two things that make a retained surface honest while its socket is down;
 *   - expose the shared `{ loading, state, dispatch, undo, redo, undoState,
 *     connStatus, actorId }` surface — the same field names both rooms surface, though
 *     `loading`/`state`'s CONTRACT differs by mode (see their own doc below).
 *
 * The thin per-domain wrappers (`useRemoteRoster` / `useEncounter`) supply the
 * `factory` (which names the room and wires the real `WebSocket`), the dependency
 * list that triggers a reconnect, and an `options` bag (the not-connected dispatch
 * error + whether to retain) — and the roster wrapper layers its `computed` view on
 * top. The controller it returns is exposed so a wrapper can read controller-specific
 * methods (e.g. the roster's `computeAll`).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSyncExternalStore } from "react";
import type { UndoState } from "@lancer-vtt/domain";
import type { RemoteController, DispatchResult } from "./remoteController";
import { LINK_DOWN_NOTE, reconnectDelayMs, RECONNECT_MAX_ATTEMPTS, type ConnStatus } from "./roomWs";
import { useLinkReport } from "./linkHealth";

const EMPTY_UNDO: UndoState = {
  canUndo: false,
  canRedo: false,
  undoLabel: null,
  redoLabel: null,
};

/** What `useRoomController` returns — the shared command-room React surface. */
export type UseRoomController<S, Cmd, C extends RemoteController<S, Cmd>> = {
  /**
   * **With `retainStateAcrossReconnect: true`** (the roster and encounter rooms):
   * true only until authoritative state has EVER arrived for the current identity — the
   * honest first-load blank. This is **not** "currently connected": once state has
   * arrived once, `loading` stays `false` through every later reconnect (retained state
   * fills in, see {@link state}), even while the socket is down. Read {@link connStatus}
   * for live connectivity — a reconnecting-but-previously-loaded surface is
   * `loading: false, connStatus: "disconnected"`, which is exactly the "keep the board
   * up, show a banner" case this mode exists for. Flips back to `true` only on a genuine
   * identity change (the caller's `deps` change, e.g. switching encounters) — never on a
   * reconnect alone.
   *
   * **With `retainStateAcrossReconnect: false`** (the default; no shipped room uses it):
   * `true` whenever there is no live, WELCOMED controller
   * (`controller === null || !controller.connected`). A reconnect's fresh, unwelcomed
   * controller flips this back to `true` immediately, same as a genuine first load.
   */
  loading: boolean;
  /**
   * **With `retainStateAcrossReconnect: true`**: the last WELCOMED authoritative state —
   * `null` only before the first welcome for the CURRENT identity. A fresh, unwelcomed
   * controller (built on every reconnect) reports a real-but-meaningless placeholder
   * snapshot, not this field — this stays pinned to the last state a genuine `*_welcome`
   * seeded, under the same `deps`, so the caller always has something true to render
   * across a drop. Discarded (back to `null`) the moment `deps` changes — a different
   * encounter/roster identity must never inherit the old one's board.
   *
   * **With `retainStateAcrossReconnect: false`** (the default): the live controller's raw
   * snapshot state (`controller?.getSnapshot()?.state ?? null`). On a reconnect this is
   * the FRESH, unwelcomed controller's placeholder state (non-null — every
   * `RemoteController` subclass seeds one before its first welcome), not the old value
   * and not `null`; callers pair this with `loading` above, never `state === null`, to
   * detect "not yet real."
   */
  state: S | null;
  /**
   * Optimistically apply + send a command (LWW-reconciled to the server's broadcast). **Refused
   * with {@link LINK_DOWN_NOTE}, with no optimistic apply, while the socket is not `connected`.**
   * Between a close and the reconnect the old controller still holds `you`, so without the gate a
   * write would apply locally, send into a dead socket, and snap back when the fresh controller
   * welcomes — a lost action with no feedback.
   */
  dispatch: (command: Cmd) => DispatchResult;
  /** Ask the server to undo the last command in `scope` (default `""`, the only scope the non-roster rooms use). False while not connected. */
  undo: (scope?: string) => boolean;
  /** Ask the server to redo the last undone command in `scope` (default `""`). False while not connected. */
  redo: (scope?: string) => boolean;
  /**
   * Is the socket live right now? Read at call time, not render time, so a wrapper can gate a
   * write fired in the same tick as the close event. `useEncounter` gates its intent and prompt
   * sends on it.
   */
  isLive: () => boolean;
  /**
   * This actor's server-reported undo/redo availability for `scope`. An **accessor**,
   * not a value: a roster window reads its own `mech:`/`pilot:` scope so the two editors'
   * UNDO/REDO reflect only their own subject; the encounter room reads `undoState()` (the
   * single default scope).
   */
  undoState: (scope?: string) => UndoState;
  /** Live ws connection status (connecting → connected once welcomed → disconnected). */
  connStatus: ConnStatus;
  /**
   * **This drop has outlasted the automatic fast-retry budget — an escalation signal, NOT "we
   * gave up".** All `RECONNECT_MAX_ATTEMPTS` backoff steps (~45s) have failed, so this is no
   * longer a blip; the hook keeps retrying at the 15s cap forever, and a wake-up
   * (`online`/`visibilitychange`) retries immediately.
   *
   * **Never let the copy this drives tell the user to reload.** Since the hook retries forever,
   * a reload would destroy a GM's mid-fight session moments before the app heals itself. What
   * the flag is for is *legibility*: a retained board renders a complete, plausible, permanently
   * stale fight, so "still relinking" and "relinking" must not look identical.
   *
   * Deliberately a SEPARATE flag rather than a fourth `ConnStatus` value: `connStatus` is read in
   * several places as "is the socket live right now", and widening that union would silently
   * change every one of those reads. `linkLost: true` always implies `connStatus ===
   * "disconnected"`; the reverse does NOT hold — an ordinary, still-retrying drop is
   * `disconnected` with `linkLost: false`.
   */
  linkLost: boolean;
  /**
   * **Reconnect NOW** — abandon the pending backoff timer, reset the attempt budget, and
   * re-run the connect effect immediately.
   *
   * The automatic retry already recovers on its own, so this is about *agency*, not
   * capability: at the 15s cap a GM mid-fight is staring at a stale board with no way to express
   * "try again, now." Safe to call at any time and idempotent in effect — a call while already
   * connected still tears down and rebuilds the connection, which is the honest reading of a
   * user asking to reconnect; callers that only want it while down should gate on
   * {@link connStatus}.
   */
  reconnect: () => void;
  /**
   * Our server-assigned actor id, or null until the welcome arrives. **With retention on**, the last
   * welcomed id under the current identity, like {@link state}: the slot does not change across a
   * reconnect, and a surface that lost it for the length of a drop would stop offering the viewer
   * their own units (no drag, no menu) instead of letting the write gate refuse with its note.
   */
  actorId: string | null;
  /** The live controller (null until the effect constructs it) — for domain-specific reads. */
  controller: C | null;
}

/** `useRoomController`'s options bag — folded into one object (rather than a 4th positional
 *  param) to stay within `CODE_STYLE.md`'s lint-gated `max-params` cap of 3. */
export type UseRoomControllerOptions = {
  /** Returned by `dispatch` before a controller exists. */
  notConnectedError: string;
  /**
   * Retain the last WELCOMED state across a controller swap (reconnect) instead of
   * resetting `loading`/`state` to their honest first-load values — see {@link
   * UseRoomController}'s `loading`/`state` doc for the full two-mode contract. **Default
   * `false`.** Both shipped rooms opt in (roster and encounter). What makes a retained room safe
   * is that a drop is still visible and still refuses writes: every room reports its link to the
   * app-wide pill (`lib/linkHealth.tsx`), and `dispatch` refuses while not connected, so a
   * retained surface is never writable-looking and dead.
   */
  retainStateAcrossReconnect?: boolean;
};

/**
 * Open (or reuse the injected) connection and adapt its {@link RemoteController}
 * into React. `factory` constructs the controller over a transport and returns a
 * `close` teardown; `deps` is the dependency list that triggers a reconnect (the
 * caller folds in compendium/url/id identity); `options` carries the not-connected
 * dispatch error and the opt-in retention mode (see {@link UseRoomControllerOptions}).
 */
export function useRoomController<S, Cmd, C extends RemoteController<S, Cmd>>(
  factory: (onStatus: (s: ConnStatus) => void) => { controller: C; close: () => void },
  deps: ReadonlyArray<unknown>,
  options: UseRoomControllerOptions,
): UseRoomController<S, Cmd, C> {
  const { notConnectedError, retainStateAcrossReconnect: retain = false } = options;
  const [controller, setController] = useState<C | null>(null);
  const [connStatus, setConnStatus] = useState<ConnStatus>("connecting");
  // Keep the latest controller in a ref so the action callbacks stay stable
  // across the connecting→connected swap without going stale.
  const controllerRef = useRef<C | null>(null);
  controllerRef.current = controller;

  // The factory is invoked inside the effect; `deps` is the documented reconnect
  // trigger, so the factory itself is intentionally excluded from the dep list.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const factoryRef = useRef(factory);
  factoryRef.current = factory;

  // ── Reconnect ────────────────────────────────────────────────────────────────
  // Losing the socket is normal — see `startKeepalive` in `roomWs.ts` — and a closed
  // room socket left closed leaves the surface silently stale (a board that stops
  // updating, a roster that stops saving) with no signal beyond the status pill. So
  // re-open it: bumping this nonce re-runs the connect effect, which is the one place
  // that knows how to build a connection; the effect needs no reconnect-specific branch.
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const attemptRef = useRef(0);
  /** True once the automatic fast-retry budget is spent — see `linkLost` on
   *  {@link UseRoomController}. State, not a ref, because the caller renders it. */
  const [linkLost, setLinkLost] = useState(false);
  /** Live mirror of `connStatus` for the wake-up revival listener below, which is mounted ONCE
   *  (empty deps, so it never re-subscribes) and would otherwise close over the first render's
   *  value forever. Render-time ref write, matching `controllerRef` above. */
  const connStatusRef = useRef(connStatus);
  connStatusRef.current = connStatus;
  /** True once the CURRENT identity has been welcomed at least once — what makes a later
   *  not-connected a drop (the link pill's business) rather than a first load (the surface's own
   *  loading card). Cleared on a deps change, like the retained state. */
  const hadLinkRef = useRef(false);
  /** True while the CURRENT teardown is ours (unmount / dep change), so its close isn't a drop. */
  const teardownRef = useRef(false);

  // ── Retained state across a controller swap ────────────────────────────────────
  // The last non-null snapshot state seen under the CURRENT `deps` identity, updated
  // every render below and read as `state`'s fallback. Cleared only when `deps` itself
  // changes (a genuine identity change — different encounter/roster/url/token), never
  // on a reconnect (`reconnectNonce` alone): the effect below is the ONE place that can
  // tell the two apart, since both share the same dependency array.
  const retainedStateRef = useRef<S | null>(null);
  /** The last welcomed actor id under the current `deps` — {@link retainedStateRef}'s twin. */
  const retainedActorIdRef = useRef<string | null>(null);
  /** The `deps` this effect last ran with, compared by shallow identity so the effect can
   *  tell "the caller's identity changed" (clear retained state) from "only the
   *  reconnect nonce bumped" (keep it). `null` before the first run. */
  const prevDepsRef = useRef<ReadonlyArray<unknown> | null>(null);

  useEffect(() => {
    const depsChanged = prevDepsRef.current !== null && !shallowEqual(prevDepsRef.current, deps);
    prevDepsRef.current = deps;
    if (depsChanged) {
      // A different identity (e.g. a different encounter id) must never render under the
      // old one's last-known board — discard, so `loading`/`state` go back to their honest
      // first-load values for the new identity until IT welcomes us.
      retainedStateRef.current = null;
      retainedActorIdRef.current = null;
      hadLinkRef.current = false;
      // A new identity gets a fresh retry budget: the old one's spent attempts say nothing about
      // this one's reachability. Without this reset, a room entered after a previous room gave up
      // would inherit `attemptRef` at the cap and report `linkLost` on its FIRST drop, never
      // retrying once.
      attemptRef.current = 0;
      setLinkLost(false);
    }
    teardownRef.current = false;
    // Scope the status callback to THIS connection: `close()` below delivers its close
    // event after the next connection may already be live, and an unscoped callback
    // would let the old socket write `disconnected` over it.
    let live = true;
    const onStatus = (s: ConnStatus): void => {
      if (!live) return;
      // Synchronously too, so the write gate refuses a dispatch fired before the re-render lands.
      connStatusRef.current = s;
      setConnStatus(s);
    };
    onStatus("connecting");
    const { controller: c, close } = factoryRef.current(onStatus);
    setController(c);

    // The controller flips `connected` on its welcome; bridge that into our
    // status. Subscribe so re-renders also re-read it (it notifies on every
    // change, welcome included).
    const unsub = c.subscribe(() => {
      if (c.connected) onStatus("connected");
    });
    if (c.connected) onStatus("connected");

    return () => {
      live = false;
      teardownRef.current = true;
      unsub();
      close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reconnectNonce]);

  // Re-open on an unexpected close, paced by the shared backoff, **forever**. A `connected`
  // status resets the budget, so a long session that drops repeatedly gets a fast first retry
  // every time rather than inheriting the tail of an old backoff.
  //
  // Never reintroduce a give-up here: a room socket has no escape hatch (unlike `/ws/game`, which
  // falls through to a reachable login form) and nothing remounts it mid-fight, so giving up is
  // unrecoverable without a page reload — a laptop that slept through a turn would come back to a
  // board that never heals. `reconnectDelayMs` caps at 15s, so retrying forever costs one request
  // per 15s while the tab is open.
  useEffect(() => {
    if (connStatus === "connected") {
      attemptRef.current = 0;
      hadLinkRef.current = true;
      setLinkLost(false);
      return;
    }
    if (connStatus !== "disconnected") return;
    if (teardownRef.current) return; // our own teardown, not a drop
    if (attemptRef.current >= RECONNECT_MAX_ATTEMPTS) {
      // Still retrying — but the *automatic fast* budget is spent, which is the honest moment to
      // tell the user this is no longer a blip and to offer them the manual control. See
      // `linkLost`'s doc: this is an escalation signal, NOT "we gave up".
      setLinkLost(true);
    }
    // Clamped so a tab left open overnight can't drift `attemptRef` toward `2 ** n` overflow in
    // `reconnectDelayMs` — past the cap every further attempt is the same 15s anyway.
    const delay = reconnectDelayMs(Math.min(attemptRef.current, RECONNECT_MAX_ATTEMPTS));
    attemptRef.current += 1;
    const timer = setTimeout(() => setReconnectNonce((n) => n + 1), delay);
    return () => clearTimeout(timer);
  }, [connStatus]);

  // Wake-up revival. The backoff above tops out at 15s, so a machine coming back from sleep or
  // regaining wifi would sit stale for up to that long before anyone tries. Both signals mean
  // "the environment just changed, stop waiting and try NOW": `online` fires when the network
  // returns, `visibilitychange` when the tab is foregrounded again (a slept laptop typically
  // fires the latter first). Mirrors the listener `useGameSession` carries for the login socket.
  // Resetting `attemptRef` is the point — it collapses the backoff back to a ~1s first retry
  // instead of inheriting a 15s tail.
  useEffect(() => {
    const revive = (): void => {
      if (teardownRef.current) return;
      if (connStatusRef.current === "connected") return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      attemptRef.current = 0;
      setReconnectNonce((n) => n + 1);
    };
    window.addEventListener("online", revive);
    document.addEventListener("visibilitychange", revive);
    return () => {
      window.removeEventListener("online", revive);
      document.removeEventListener("visibilitychange", revive);
    };
  }, []);

  // The manual control's action. Resetting `attemptRef` matters as much as the nonce bump:
  // without it the NEXT automatic retry after a failed manual one would inherit the 15s tail, so
  // a user who pressed the button would be punished with a longer wait than one who didn't.
  // Stable identity (empty deps) so a consumer can hand it straight to `onClick` without
  // re-rendering the subtree every time the status changes.
  const reconnect = useMemo(() => {
    return (): void => {
      attemptRef.current = 0;
      // Clear the escalation with the budget it describes. `linkLost` means "this drop outlasted
      // the fast-retry budget"; a manual press grants a FRESH budget, so leaving the flag set
      // would make it contradict its own definition — and the UI would keep claiming "still
      // relinking" while a brand-new ~1s attempt was in flight, giving the press no feedback at
      // all. If the new budget is spent too, the effect re-escalates on its own.
      setLinkLost(false);
      setReconnectNonce((n) => n + 1);
    };
  }, []);

  const subscribe = useMemo(() => {
    return (cb: () => void) => controller?.subscribe(cb) ?? (() => {});
  }, [controller]);

  const snapshot = useSyncExternalStore(
    subscribe,
    () => controller?.getSnapshot() ?? null,
  );

  // Retain the latest WELCOMED state — plain ref mutation during render, matching this
  // file's `controllerRef.current = controller` pattern just above. Gated on `retain` so a
  // non-retaining room doesn't accumulate a ref nothing reads.
  //
  // **Gate on `controller.connected` (welcomed), NOT on "`snapshot.state` is non-null".**
  // Every `RemoteController` subclass seeds its history from a real, non-null placeholder
  // (`RemoteEncounterController`'s `emptyEncounter(...)`) BEFORE the welcome ever lands, so
  // `snapshot?.state` is structurally truthy from the moment a controller exists, welcomed
  // or not. Retaining on that would overwrite genuine last-good state with the FRESH
  // post-reconnect controller's meaningless empty placeholder the instant it's swapped in —
  // the opposite of retention. `connected` (`this.ready`, set once in the welcome handler
  // and never unset for that instance) is the only true signal that `snapshot.state` is real.
  // Runs every render; a harmless no-op once retained already equals the live value.
  //
  // The clearing half (on a genuine deps change) lives in the connect effect above, and
  // **effects run AFTER render**: on a `deps` change, the render carrying the NEW `deps`
  // still sees the OLD controller and OLD retained value for one render; the FOLLOWING
  // render is the first to show the honest first-load values for the new identity. The shell's
  // stage keys `CombatApp` on `encounterId`, so today a scene change remounts instead; this path
  // covers any host that does not.
  if (retain && controller?.connected === true && snapshot?.state != null) {
    retainedStateRef.current = snapshot.state;
    retainedActorIdRef.current = controller.actorId;
  }

  // The write gate: see `dispatch`'s doc. Undo and redo are writes too.
  const actions = useMemo(() => {
    const isLive = (): boolean => connStatusRef.current === "connected";
    return {
      isLive,
      dispatch: (command: Cmd): DispatchResult => {
        const c = controllerRef.current;
        if (c === null) return { ok: false as const, error: notConnectedError };
        if (!isLive()) return { ok: false as const, error: LINK_DOWN_NOTE };
        return c.dispatch(command);
      },
      undo: (scope?: string): boolean => isLive() && (controllerRef.current?.undo(scope) ?? false),
      redo: (scope?: string): boolean => isLive() && (controllerRef.current?.redo(scope) ?? false),
      // An accessor so a scoped caller (the roster editors) reads its own subject's
      // availability; re-evaluated each render, which the snapshot bump on `undo_state`
      // drives. The non-roster callers read `undoState()` (the default scope).
      undoState: (scope?: string): UndoState => controllerRef.current?.undoState(scope) ?? EMPTY_UNDO,
    };
  }, [notConnectedError]);

  // Report to the app-wide link pill: down only after this identity has been live, so a first
  // load stays the surface's own loading card's business.
  useLinkReport(hadLinkRef.current && connStatus !== "connected", hadLinkRef.current && linkLost, reconnect);

  return {
    // Two-mode contract — see `UseRoomController.loading`'s own doc for the full text.
    // Retaining ("never had state"): true only before the first welcome for the CURRENT
    // identity; a reconnect that already retained state keeps this `false`. Deliberately
    // NOT "currently connected" (that's `connStatus`). Non-retaining (default): true
    // whenever there's no live, welcomed controller — flips back to `true` on every
    // reconnect, same as a first load.
    loading: retain ? retainedStateRef.current === null : controller === null || !controller.connected,
    // Retaining: the last WELCOMED state — never the live `snapshot.state` directly, since
    // (per the retention block above) a fresh, unwelcomed controller's snapshot is a
    // real-but-meaningless placeholder, not `null`, and would otherwise leak through here
    // for the one-or-more renders between a reconnect's controller swap and its welcome.
    // Non-retaining (default): the live controller's raw snapshot state — never falls back
    // to a retained value.
    state: retain ? retainedStateRef.current : (snapshot?.state ?? null),
    dispatch: actions.dispatch,
    undo: actions.undo,
    redo: actions.redo,
    undoState: actions.undoState,
    isLive: actions.isLive,
    connStatus,
    linkLost,
    reconnect,
    actorId: retain ? (controller?.connected === true ? controller.actorId : retainedActorIdRef.current) : (controller?.actorId ?? null),
    controller,
  };
}

/**
 * Shallow element-wise equality for the connect effect's `deps` array — the ONLY thing
 * that distinguishes "the caller handed us a different identity" from "only
 * `reconnectNonce` bumped," since both share one dependency array. `deps` entries are
 * primitives/stable references the caller already folds identity into (encounter id,
 * url, compendium, token) — `Object.is` per-element is the same notion of "changed" React
 * itself uses for a plain dependency array.
 */
function shallowEqual(a: ReadonlyArray<unknown>, b: ReadonlyArray<unknown>): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!Object.is(a[i], b[i])) return false;
  }
  return true;
}
